import React from 'react';
import { Chip, Paper, useMediaQuery, useTheme } from '@material-ui/core';
import { Link, useLocation } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  Tooltip,
  Legend,
  Cell,
  LabelList,
} from 'recharts';
import { useHistory } from 'react-router-dom';
import { Issue, KnownIssues } from '../../Alerts/types';
import { WithClassName } from '../../../types';
import { chartColor } from '../provider';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { MedisanteTargetSystem } from '../../TargetSystems/types';
import { useConfigurationWarning } from '../../Alerts/hooks';
import { MedisanteOrgReport } from '../types';

const useStyles = makeStyles((theme: Theme) => ({
  placeholder: {
    textAlign: 'center',
  },
  noWarningsPanel: {
    height: 180,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clickable: {
    '& .recharts-surface': {
      cursor: 'pointer',
    },
  },

  legend: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'stretch',
      minWidth: '30ch',
    },
  },
  legendItem: {
    textDecoration: 'none',
    color: 'inherit',
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 30ch',
      paddingRight: '10px',
    },

    [theme.breakpoints.down('xs')]: {
      fontSize: '0.8rem',
    },
  },
  customTooltip: {
    padding: '15px',
  },
  countChip: {
    marginLeft: theme.spacing(1),
  },
  barChart: {
    '& .customBarChartLabel': {
      display: 'none',
    },
  },
}));

const CustomTooltip = ({ active, payload, label, ...rest }: any) => {
  const classes = useStyles();
  if (active && payload && payload.length > 0) {
    return (
      <Paper className={classes.customTooltip}>
        <Typography variant="body2">
          {label}
          <br />
          Count: {payload[0].value}
        </Typography>
      </Paper>
    );
  }

  return null;
};

export interface Props {
  knownIssues?: Partial<KnownIssues>;
  targetSystems?: MedisanteTargetSystem[];
  reports: MedisanteOrgReport[];
  disabledClick?: boolean;
}

function AlertsBarChart({
  knownIssues,
  targetSystems,
  className,
  reports,
  disabledClick = false,
}: WithClassName<Props>) {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const location = useLocation();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const statusCodes =
    targetSystems?.filter(
      (ts) => ts.lastStatusCode && ts.lastStatusCode >= 400
    ) || [];

  const incompatibleTs = useConfigurationWarning(reports, targetSystems || []);

  const barChartData = Object.values(knownIssues || {})
    .filter((v) => v)
    .map((i) => {
      const issue = i as Issue;
      return {
        name: issue.label,
        deviceCount: issue.deviceCount,
        severity: issue.severity,
        link: issue.link,
        color: issue.barChartColor,
      };
    });

  if (statusCodes.length > 0) {
    barChartData.push({
      name: 'Status warning',
      deviceCount: statusCodes.length,
      severity: 'error',
      link: '/webhooks?',
      color: 'black',
    });
  }

  if (incompatibleTs.length > 0) {
    barChartData.push({
      name: 'Configuration warning',
      deviceCount: incompatibleTs.length,
      severity: 'error',
      link: '/webhooks?configurationWarning=true',
      color: '#9f4a0a',
    });
  }
  const numberOfIssues = barChartData.reduce(
    (result, item) => (result += item.deviceCount),
    0
  );

  const shouldShowChart = barChartData.length > 0;

  const maxDeviceCount = barChartData.reduce(
    (max, data) => (max > data.deviceCount ? max : data.deviceCount),
    0
  );

  const ticksCount = barChartData.length === 1 || maxDeviceCount <= 4 ? 1 : 4;

  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className={classes.legend}>
        {payload.map((entry: any, index: number) => (
          <Typography
            color="textPrimary"
            variant="caption"
            key={`item-${index}`}
          >
            {disabledClick ? (
              <>
                <svg width={15} height={15}>
                  <rect width={15} height={15} style={{ fill: entry.color }} />
                </svg>{' '}
                {entry.value}
              </>
            ) : (
              <Link to={entry.link} className={classes.legendItem}>
                <svg width={15} height={15}>
                  <rect width={15} height={15} style={{ fill: entry.color }} />
                </svg>{' '}
                {entry.value}
              </Link>
            )}
          </Typography>
        ))}
      </div>
    );
  };

  const onBarClick = (data: any) => {
    console.log(data);
    if (disabledClick) return;
    if (
      data &&
      data.activePayload &&
      data.activePayload[0] &&
      data.activePayload[0].payload
    ) {
      const existingQuery = location.search.replace(/\?/i, '&');
      history.push(data.activePayload[0].payload.link + existingQuery);
    }
  };

  return (
    <Paper className={className}>
      <Grid container alignItems="center">
        <Typography variant="h6">Alerts</Typography>
        <Chip
          className={classes.countChip}
          label={numberOfIssues.toLocaleString()}
          color="default"
        />
      </Grid>
      {shouldShowChart ? (
        <ResponsiveContainer height={isSmallScreen ? 400 : 200}>
          <BarChart
            height={isSmallScreen ? 400 : 160}
            data={barChartData}
            onClick={onBarClick}
            className={clsx(classes.barChart, classes.clickable)}
          >
            <XAxis
              stroke={theme.palette.text.primary}
              dataKey="name"
              type="category"
              tick={false}
            />
            <YAxis
              stroke={theme.palette.text.primary}
              dataKey="deviceCount"
              allowDecimals={false}
              tickCount={ticksCount}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: theme.palette.action.hover }}
              contentStyle={{ color: 'black' }}
            />

            <Legend
              align={isSmallScreen ? 'center' : 'left'}
              verticalAlign={isSmallScreen ? 'bottom' : 'middle'}
              layout={isSmallScreen ? 'horizontal' : 'vertical'}
              content={renderLegend}
              payload={barChartData.map((item) => ({
                id: item.name,
                type: 'square',
                value: `${item.name}`,
                color: item.color,
                link: item.link,
              }))}
            />
            <Bar dataKey="deviceCount" name="Device count" maxBarSize={80}>
              <LabelList
                dataKey="deviceCount"
                position="top"
                className="customBarChartLabel"
              />
              {barChartData.map((entry, _) => {
                return <Cell key={entry.name} fill={entry.color} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className={classes.noWarningsPanel}>
          <Typography
            variant="h5"
            color="textSecondary"
            className={classes.placeholder}
          >
            <CheckCircleIcon
              style={{
                color: chartColor('default'),
                fontSize: '4rem',
              }}
            />
            <br />
            No alerts
          </Typography>
        </div>
      )}
    </Paper>
  );
}

export default AlertsBarChart;
